import { fmtNum, roDate, type MarketProduct, type SupplierRef, t, intlLocale } from '@procumi/shared';

/**
 * Bucățile mici din pagina Prețuri: punctul furnizorului, săgeata variației,
 * graficul de pe rând și bara minim–maxim.
 *
 * **Culorile furnizorilor.** Doar patru: atâtea au trecut validatorul de
 * daltonism pe toate perechile (oricare doi furnizori pot sta în același
 * clasament). Albastru cu mov, de pildă, nu se deosebesc. Ceilalți furnizori au
 * punct gri, iar numele stă mereu lângă punct, deci culoarea doar ajută.
 * Verdele și roșul rămân ale banilor (mai ieftin / mai scump), nu ale cuiva; al
 * patrulea furnizor e liliachiul mărcii, nu verde (validat pe toate perechile).
 */
export const SUPPLIER_COLORS = ['#2B3BFF', '#EDA100', '#E87BA4', '#9AA3FF'];
const OTHER = '#9298A6';

export const supplierColor = (slots: Record<string, number>, id: string): string =>
  SUPPLIER_COLORS[slots[id] ?? -1] ?? OTHER;

/** „+1,6%”, „−0,5%”; minusul e cel tipografic, nu cratima */
export const pct = (v: number): string =>
  `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v * 100).toLocaleString(intlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** ISO → „8 octombrie” */
export const day = (iso: string): string => roDate(iso.slice(0, 10));

export const price = (v: number): string => fmtNum(v);

export function Supplier({ s, slots }: { s: SupplierRef; slots: Record<string, number> }) {
  return (
    <span className="mk-sup">
      <i className="mk-dot" style={{ background: supplierColor(slots, s.id) }} aria-hidden="true" />
      {s.name}
    </span>
  );
}

function Tri({ up }: { up: boolean }) {
  return (
    <svg width="8" height="7" viewBox="0 0 8 7" aria-hidden="true">
      <path d={up ? 'M4 0L8 7H0z' : 'M4 7L0 0h8z'} fill="currentColor" />
    </svg>
  );
}

/** Variația față de cererea trecută. Verde doar când plătești mai puțin. */
export function Change({ v }: { v: number | null }) {
  if (v == null) return <span className="mk-chg nil">{t('prima cerere')}</span>;
  if (Math.abs(v) < 0.0005)
    return (
      <span className="mk-chg same" title={t('Același preț ca la cererea trecută')}>
        = 0,0%
      </span>
    );
  const up = v > 0;
  return (
    <span
      className={`mk-chg ${up ? 'up' : 'dn'}`}
      title={t('{0} decât la cererea trecută', up ? t('mai scump') : t('mai ieftin'))}
    >
      <Tri up={up} />
      {pct(v)}
    </span>
  );
}

/**
 * Graficul de pe rând: un punct pe cerere, cu cel mai mic preț de atunci. Între
 * puncte nu știm prețul, așa că linia e stinsă și punctele se văd.
 */
export function Spark({ p, from }: { p: MarketProduct; from: string | null }) {
  const W = 128;
  const H = 34;
  const PAD = 4;
  // fără cereri în perioadă rămâne ultima, mai veche: axa pornește de la ea
  const first = new Date(p.points[0]!.at).getTime();
  const t0 = from ? Math.min(new Date(from).getTime(), first) : first;
  const t1 = Date.now();
  const vals = p.points.map((x) => x.price);
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const x = (at: string): number =>
    t1 === t0 ? W / 2 : PAD + ((new Date(at).getTime() - t0) / (t1 - t0)) * (W - 2 * PAD);
  const y = (v: number): number => (hi === lo ? H / 2 : H - PAD - ((v - lo) / (hi - lo)) * (H - 2 * PAD));
  const pts = p.points.map((pt) => ({ pt, x: x(pt.at), y: y(pt.price) }));
  return (
    <svg
      className="mk-spark"
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={t(
        '{0} {1}, între {2} și {3} lei pe {4}',
        p.points.length,
        p.points.length === 1 ? t('cerere') : t('cereri'),
        price(lo),
        price(hi),
        p.unit,
      )}
    >
      <line x1={PAD} x2={W - PAD} y1={H - 1} y2={H - 1} className="base" />
      {pts.length > 1 ? <polyline points={pts.map((q) => `${q.x},${q.y}`).join(' ')} /> : null}
      {pts.map((q, i) => (
        <circle
          key={q.pt.rfqId}
          cx={q.x}
          cy={q.y}
          r={i === pts.length - 1 ? 3.5 : 2.2}
          className={i === pts.length - 1 ? 'last' : ''}
        >
          <title>
            {t('{0}: {1} lei/{2}, {3}', day(q.pt.at), price(q.pt.price), p.unit, q.pt.supplier.name)}
          </title>
        </circle>
      ))}
    </svg>
  );
}

/** Unde stă prețul de acum între cel mai mic și cel mai mare din perioadă. */
export function RangeBar({ p }: { p: MarketProduct }) {
  const pos = p.hi === p.lo ? 0.5 : (p.now - p.lo) / (p.hi - p.lo);
  return (
    <div
      className="mk-range"
      title={t('Prețul de acum, între cel mai mic și cel mai mare primit în perioadă')}
    >
      <div className="track">
        <span style={{ left: `calc(${(pos * 100).toFixed(1)}% - 1.5px)` }} />
      </div>
      <div className="ends">
        <span>{price(p.lo)}</span>
        <span>{price(p.hi)}</span>
      </div>
    </div>
  );
}
